import React, { useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Alert, Button, Spinner } from '@nimbus-ds/components';
import { useTranslationWithPrefix } from 'commons/utils';
import {
  dataDogLogger,
  DataDogLoggerActionTypes,
} from 'commons/utils/dataDogLogger';
import { getProofOfLifeErrorTranslation } from 'commons/utils/getKycError';
import { PendingTrialPeriodAlertPage } from '../PendingTrialPeriodAlert';

interface InterfacePendingProofOfLifeAlert {
  proofOfLifeUrl?: string;
  isProofOfLifeUrlLoading?: boolean;
  errorCode?: string;
  page: PendingTrialPeriodAlertPage;
}

const PendingProofOfLifeAlert = ({
  proofOfLifeUrl,
  isProofOfLifeUrlLoading,
  errorCode,
  page,
}: InterfacePendingProofOfLifeAlert): JSX.Element | null => {
  const { t } = useTranslationWithPrefix(
    'configuration.personalData.kycV2.pending_documentation',
  );
  const [showAlert, setShowAlert] = useState(true);
  const { translationKey: translationPrefix, isMapped } =
    getProofOfLifeErrorTranslation(errorCode);

  useEffect(() => {
    track('pago-nube-prueba-de-vida-vencida-view', {
      page,
    });
  }, [page]);

  useEffect(() => {
    if (errorCode && !isMapped) {
      dataDogLogger.generate({
        actionMessage: 'kyc_unmapped_error',
        actionOwner: 'PendingProofOfLifeAlert',
        actionType: DataDogLoggerActionTypes.Error,
        actionData: { errorCode, documentName: 'proof_of_life' },
      });
    }
  }, [errorCode, isMapped]);

  // Without a url there is nothing to open, so the button says so instead of
  // swallowing the click: dlocal sometimes has no proof of life url yet, and a
  // button that silently does nothing reads as a broken app.
  const isDisabled = !proofOfLifeUrl;

  const handleOnClick = () => {
    if (!proofOfLifeUrl) return;
    track('pago-nube-prueba-de-vida-vencida-click', {
      page,
    });
    track('pago-nube-prueba-de-vida-remediancion-reintentar');
    const w = window.open(proofOfLifeUrl, '_blank', 'noopener,noreferrer');
    if (w) {
      w.opener = null;
      setShowAlert(false);
    }
  };

  return (
    <Alert
      show={showAlert}
      appearance="danger"
      title={t(`${translationPrefix}.title`)}
      data-testid="pending-proof-of-life-alert"
    >
      {t(`${translationPrefix}.description`)}
      <Button
        appearance="neutral"
        onClick={handleOnClick}
        disabled={isDisabled}
      >
        {isProofOfLifeUrlLoading && <Spinner size="small" />}
        {t(`${translationPrefix}.validateButton`)}
      </Button>
    </Alert>
  );
};

export default PendingProofOfLifeAlert;
